import { QuerySnapshot, QueryDocumentSnapshot } from '@firebase/firestore';
import { AccessType, ContentGroupType } from './types/others';
import { Author } from './types/author';
import { PriceTier } from './types/priceTier';

export const parseContent = <T>(doc: QueryDocumentSnapshot<T>): { id?: string; [key: string]: any } | undefined => {
  if (!doc || !doc.exists?.()) {
    return undefined;
  }

  const data = doc.data();
  return { id: doc.id, ...data };
};

export const parseContents = <T>(value: QuerySnapshot<T>) => {
  if (!value?.docs) {
    return [];
  }

  return value?.docs?.map((doc) => parseContent(doc))?.filter((item) => item !== undefined);
};

export const getDisplayNames = {
  pillarType: (pillarType: string | undefined) => {
    if (!pillarType) {
      return undefined;
    }
    switch (pillarType) {
      case 'intellectual':
        return 'Intellectual';
      case 'relationship':
        return 'Relationship';
      case 'physical':
        return 'Physical';
      case 'financial':
        return 'Financial';
      case 'emotional':
        return 'Emotional';
      default:
        return pillarType;
    }
  },

  pillarExpertise: (pillarExpertise: string | undefined) => {
    if (!pillarExpertise) {
      return undefined;
    }
    switch (pillarExpertise) {
      case 'expert':
        return 'Expert';
      case 'advanced':
        return 'Advanced';
      case 'beginner':
        return 'Beginner';
      default:
        return pillarExpertise;
    }
  },

  accessType: (accessType: AccessType | undefined) => {
    if (!accessType) {
      return undefined;
    }
    switch (accessType) {
      case 'free':
        return 'Free';
      case 'subscription':
        return 'Subscription';
      case 'paidSubscription':
        return 'Paid subscription';
      case 'purchase':
        return 'Purchase';
      case 'noPayment':
        return 'No payment';
      case 'yearlySubscriptionOnly':
        return 'Yearly subscription only';
      default:
        return accessType;
    }
  },

  author: (id: string | undefined, authors?: Author[]) => {
    if (!id) {
      return undefined;
    }
    const author = authors?.filter((item) => item?.id === id)?.[0];
    let prefix = 'Expert ';
    let suffix = '';
    if (author && author?.name && author?.name?.length > 0) {
      prefix = author?.name + ' (';
      suffix = ')';
    }
    return prefix + 'ID: ' + id + suffix;
  },

  contentGroupType: (contentGroupType: ContentGroupType | undefined) => {
    if (!contentGroupType) {
      return undefined;
    }
    switch (contentGroupType) {
      case 'collection':
        return 'Collection';
      case 'bundle':
        return 'Bundle';
      default:
        return contentGroupType;
    }
  },

  priceTier: (productId: string | undefined, priceTiers?: PriceTier[]) => {
    if (!productId) {
      return undefined;
    }
    const priceTier = priceTiers?.filter((item) => item?.productId === productId)?.[0];
    if (priceTier && priceTier?.name && priceTier?.name?.length > 0) {
      return `${priceTier?.name} (${priceTier?.productId})`;
    }
    return productId;
  },

  contentType: (contentType: string | undefined) => {
    if (!contentType) {
      return undefined;
    }
    switch (contentType) {
      case 'lesson':
        return 'Lesson';
      case 'story':
        return 'Story';
      default:
        return contentType;
    }
  },

  sectionType: (sectionType: string | undefined) => {
    if (!sectionType) {
      return undefined;
    }

    switch (sectionType) {
      case 'todaysPicks':
        return "Today's Picks";
      case 'updateData':
        return 'Update Banner';
      case 'survey':
        return 'Survey';
      case 'collectionList':
        return 'List of Collections';
      case 'collectionListMore':
        return 'List of Collections (Show More)';
      case 'collectionSection':
        return 'One Collection Section';
      case 'userNewLessons':
        return 'User Greatest Lessons Collection';
      case 'userMissedSessions':
        return 'User Missed Sessions';
      case 'journal':
        return 'Journal';
      case 'journalTab':
        return 'Journal Tab';
      case 'unlockSubscription':
        return 'Unlock Subscription';
      case 'bundleList':
        return 'List of Bundles';
      case 'notes':
        return 'Notes';
      case 'stats':
        return 'Stats';
      case 'yourPurchases':
        return 'Your Purchases';
      case 'savedContentGroups':
        return 'Saved Content Groups';
      case 'gifts':
        return 'Gifts';
      case 'health':
        return 'Health';
      case 'dailyAluxSessions':
        return 'Daily Alux Sessions';
      case 'downloads':
        return 'Downloads';
      case 'favorites':
        return 'Favorites';
      case 'goalsList':
        return 'Goals';
      case 'recentlyPlayed':
        return 'Recently Played';
      case 'recentlyPlayedAuthors':
        return 'Recently Played Authors';
      case 'userPaths':
        return 'User Paths';
      case 'learningPath':
        return 'Learning Path';
      case 'learningPathProgress':
        return 'Learning Path Progress';
      case 'goalBasedCollections':
        return 'Goal Based Collections';
      case 'trendingAuthors':
        return 'Trending Authors';
      case 'trendingCommunities':
        return 'Trending Communities';
      case 'trendingTopics':
        return 'Trending Topics';
      case 'weeklyStrategicReps':
        return 'Weekly Strategic Reps';
      default:
        return 'Unknown section type';
    }
  },

  cellType: (cellType: string | undefined) => {
    if (!cellType) {
      return undefined;
    }

    switch (cellType) {
      case 'largeCarousel':
        return 'Large Carousel';
      case 'wideList':
        return 'Wide List';
      case 'defaultCarousel':
        return 'Default Carousel';
      case 'authorCarousel':
        return 'Author Carousel';
      case 'pillarCard':
        return 'Pillar';
      case 'twoColumnsList':
        return 'List with Two Columns';
      case 'simpleCard':
        return 'Simple Card';
      case 'listCard':
        return 'List Card';
      case 'simpleCard2':
        return 'Simple Lesson Card ';
      case 'playProgressCard':
        return 'Play Progress Card';
      default:
        return 'Unknown cell type';
    }
  },

  seeAllCellType: (cellType: string | undefined) => {
    if (!cellType) {
      return undefined;
    }

    switch (cellType) {
      case 'wideList':
        return 'Wide List';
      case 'defaultCarousel':
        return 'Default Carousel';
      case 'authorCarousel':
        return 'Author Carousel';
      case 'simpleCard':
        return 'Simple Card';
      case 'twoColumnsList':
        return 'List with Two Columns';
      case 'listCard':
        return 'List Card';
      default:
        return 'Unknown cell type';
    }
  },

  fetchRuleType: (fetchRuleType: string | undefined) => {
    if (!fetchRuleType) {
      return undefined;
    }

    switch (fetchRuleType) {
      case 'static':
        return 'Static';
      case 'authors':
        return 'Authors';
      case 'pillar':
        return 'Pillar';
      case 'category':
        return 'Category';
    }
  },

  formQuestionType: (formQuestionType: string | undefined) => {
    if (!formQuestionType) {
      return undefined;
    }
    switch (formQuestionType) {
      case 'reflection':
        return 'Reflection';
      case 'journal':
        return 'Journal';
      case 'pillarSurvey':
        return 'Pillar Survey';
      default:
        return formQuestionType;
    }
  },

  lessonType: (lessonType: string | undefined) => {
    if (!lessonType) {
      return undefined;
    }
    switch (lessonType) {
      case 'dailyAlux':
        return 'Daily Alux';
      case 'ragsToRichesStory':
        return 'Rags to riches';
      case 'sleepStory':
        return 'Sleep story';
      case 'specialStory':
        return 'Special story';
      case 'wisdomStory':
        return 'Wisdom story';
      default:
        return lessonType;
    }
  },

  dealType: (dealType: string | undefined) => {
    if (!dealType) {
      return undefined;
    }
    switch (dealType) {
      case 'product':
        return 'Product';
      case 'subscription':
        return 'Subscription';
      default:
        return dealType;
    }
  },
};
